import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  configApiRef,
  useApi,
  type ConfigApi,
} from '@backstage/core-plugin-api';
import {
  Box,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Paper,
  Popper,
  Typography,
} from '@material-ui/core';
import type { PopperPlacementType } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import { tourStore, type TourState } from './tourStore';

export type TourStep = {
  title: string;
  body: string;
  /** Portal path to open before showing the step. */
  path?: string;
  /** CSS selector of the element to point at. Without one, or when it never
   *  appears, the step is shown as a card in the middle of the screen. */
  target?: string;
  placement?: PopperPlacementType;
};

export type Tour = {
  id: string;
  title: string;
  description?: string;
  steps: TourStep[];
  /** Another tour the visitor can switch to from any step. */
  switchTo?: { tour: string; label: string };
};

// How long to wait for a step's target after navigating. Entity pages render
// their tabs only after the catalog lookup, which can take a few seconds.
const TARGET_TIMEOUT_MS = 6000;
const SPOTLIGHT_PADDING = 6;

const useStyles = makeStyles(theme => ({
  spotlight: {
    position: 'fixed',
    zIndex: 1400,
    borderRadius: theme.shape.borderRadius,
    boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.5)',
    pointerEvents: 'none',
    transition: 'all 200ms ease',
  },
  backdrop: {
    position: 'fixed',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    zIndex: 1400,
    background: 'rgba(0, 0, 0, 0.5)',
  },
  popper: {
    zIndex: 1401,
    maxWidth: 380,
    margin: theme.spacing(1.5),
  },
  centered: {
    position: 'fixed',
    zIndex: 1401,
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 'min(440px, calc(100vw - 32px))',
  },
  card: {
    padding: theme.spacing(2, 2.5),
  },
  header: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
  },
  counter: {
    color: theme.palette.text.secondary,
    fontSize: '0.75rem',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
  },
  body: {
    margin: theme.spacing(1, 0, 2),
    whiteSpace: 'pre-line',
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
  },
}));

function readTours(config: ConfigApi) {
  return (config.getOptionalConfigArray('app.showroom.tours') ?? []).map(
    (t): Tour => ({
      id: t.getString('id'),
      title: t.getString('title'),
      description: t.getOptionalString('description'),
      switchTo: t.has('switchTo')
        ? {
            tour: t.getString('switchTo.tour'),
            label: t.getString('switchTo.label'),
          }
        : undefined,
      steps: t.getConfigArray('steps').map(s => ({
        title: s.getString('title'),
        body: s.getString('body'),
        path: s.getOptionalString('path'),
        target: s.getOptionalString('target'),
        placement: s.getOptionalString('placement') as
          | PopperPlacementType
          | undefined,
      })),
    }),
  );
}

function useTourState() {
  const [state, setState] = useState<TourState | undefined>(tourStore.get());
  useEffect(() => tourStore.subscribe(setState), []);
  return state;
}

function isVisible(el: Element) {
  const r = el.getBoundingClientRect();
  return r.width > 0 && r.height > 0;
}

// Resolves the step's target once it is on screen, or null after the timeout.
// Re-measures while the step is open, so the spotlight follows layout shifts.
function useTarget(step: TourStep | undefined, pathname: string) {
  const [el, setEl] = useState<Element | null>(null);
  const [rect, setRect] = useState<DOMRect | null>(null);
  const [settled, setSettled] = useState(false);

  useEffect(() => {
    setEl(null);
    setRect(null);
    setSettled(false);
    if (!step) return undefined;
    if (!step.target) {
      setSettled(true);
      return undefined;
    }
    if (step.path && step.path !== pathname) {
      // Still navigating. If the page never arrives (a redirect, say), fall
      // back to a centred card rather than showing nothing.
      const fallback = window.setTimeout(
        () => setSettled(true),
        TARGET_TIMEOUT_MS,
      );
      return () => window.clearTimeout(fallback);
    }
    const selector = step.target;
    const started = Date.now();
    let found: Element | null = null;
    const timer = window.setInterval(() => {
      if (!found) {
        const candidate = document.querySelector(selector);
        if (candidate && isVisible(candidate)) {
          found = candidate;
          candidate.scrollIntoView({ block: 'center', behavior: 'smooth' });
          setEl(candidate);
          setSettled(true);
        } else if (Date.now() - started > TARGET_TIMEOUT_MS) {
          window.clearInterval(timer);
          setSettled(true);
          return;
        }
      }
      if (found) {
        setRect(found.isConnected ? found.getBoundingClientRect() : null);
      }
    }, 200);
    return () => window.clearInterval(timer);
  }, [step, pathname]);

  return { el, rect, settled };
}

/**
 * Runs the showroom's guided tours, read from app.showroom.tours. Mounted once
 * at the app root. The tour named by app.showroom.autoStartTour opens on a
 * visitor's first visit; the sidebar's "Guided tour" entry opens a chooser.
 */
export const GuidedTour = () => {
  const classes = useStyles();
  const config = useApi(configApiRef);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const tours = useMemo(() => readTours(config), [config]);
  const autoStart = config.getOptionalString('app.showroom.autoStartTour');
  const state = useTourState();

  useEffect(() => {
    if (tourStore.seen() || tourStore.get()) return;
    tourStore.markSeen();
    if (autoStart && tours.some(t => t.id === autoStart)) {
      tourStore.start(autoStart);
    }
  }, [autoStart, tours]);

  const tour = tours.find(t => t.id === state?.tourId);
  const step = tour?.steps[state?.step ?? 0];

  useEffect(() => {
    if (step?.path && step.path !== pathname) navigate(step.path);
    // Only on step change: once the step is showing, the visitor may wander off.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  const { el, rect, settled } = useTarget(step, pathname);

  const close = useCallback(() => tourStore.close(), []);
  useEffect(() => {
    if (!state) return undefined;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && close();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [state, close]);

  if (!state || tours.length === 0) return null;

  if (state.tourId === null || !tour) {
    return (
      <>
        <Box className={classes.backdrop} onClick={close} />
        <Paper className={`${classes.centered} ${classes.card}`} elevation={8}>
          <Box className={classes.header}>
            <Typography variant="h6">Take a guided tour</Typography>
            <IconButton size="small" aria-label="Close" onClick={close}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
          <List>
            {tours.map(t => (
              <ListItem
                key={t.id}
                button
                onClick={() => tourStore.start(t.id)}
              >
                <ListItemText
                  primary={`${t.title} (${t.steps.length} steps)`}
                  secondary={t.description}
                />
              </ListItem>
            ))}
          </List>
        </Paper>
      </>
    );
  }

  if (!step || !settled) return null;

  const index = state.step;
  const last = index === tour.steps.length - 1;
  const switchTarget =
    tour.switchTo && tours.some(t => t.id === tour.switchTo!.tour)
      ? tour.switchTo
      : undefined;

  const card = (
    <Paper className={classes.card} elevation={8}>
      <Box className={classes.header}>
        <Box>
          <Typography className={classes.counter}>
            {tour.title} · {index + 1} of {tour.steps.length}
          </Typography>
          <Typography variant="h6">{step.title}</Typography>
        </Box>
        <IconButton size="small" aria-label="End tour" onClick={close}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
      <Typography variant="body2" className={classes.body}>
        {step.body}
      </Typography>
      <Box className={classes.footer}>
        {switchTarget ? (
          <Button
            size="small"
            color="primary"
            onClick={() => tourStore.start(switchTarget.tour)}
          >
            {switchTarget.label}
          </Button>
        ) : (
          <span />
        )}
        <Box>
          {index > 0 && (
            <Button size="small" onClick={() => tourStore.goTo(index - 1)}>
              Back
            </Button>
          )}
          <Button
            size="small"
            variant="contained"
            color="primary"
            onClick={() => (last ? close() : tourStore.goTo(index + 1))}
          >
            {last ? 'Finish' : 'Next'}
          </Button>
        </Box>
      </Box>
    </Paper>
  );

  if (el && rect) {
    return (
      <>
        <Box
          className={classes.spotlight}
          style={{
            top: rect.top - SPOTLIGHT_PADDING,
            left: rect.left - SPOTLIGHT_PADDING,
            width: rect.width + SPOTLIGHT_PADDING * 2,
            height: rect.height + SPOTLIGHT_PADDING * 2,
          }}
        />
        <Popper
          open
          anchorEl={el}
          placement={step.placement ?? 'bottom'}
          className={classes.popper}
          modifiers={{
            flip: { enabled: true },
            preventOverflow: { enabled: true, boundariesElement: 'viewport' },
          }}
        >
          {card}
        </Popper>
      </>
    );
  }

  return (
    <>
      <Box className={classes.backdrop} />
      <Box className={classes.centered}>{card}</Box>
    </>
  );
};
